import PsychologyIcon from '@mui/icons-material/Psychology';
import ReplayIcon from '@mui/icons-material/Replay';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import TryIcon from '@mui/icons-material/Try';
import { Paper, Stack, Typography } from '@mui/material';
import { formatRelative } from 'date-fns';
import { useMemo, useState } from 'react';
import { AiSmartReplyContactPlanItemStatus } from '../../../../sic-lib/enums/AiSmartReplyContactPlanItemStatus';
import { FieldValidationType } from '../../../../sic-lib/enums/FieldValidationType';
import { AiSmartReplyContactPlanState } from '../../../../sic-lib/types/Ai';
import { Contact } from '../../../../sic-lib/types/Contact';
import { UserInfo } from '../../../../sic-lib/types/UserConfig';
import { aiSmartReplyContactPlanItemStatusToLabel } from '../../../../sic-lib/utils/aiHelpers';
import { SmartReplyDialog } from '../SmartReply/SmartReplyDialog';

interface Props {
    contact: Contact;
    fieldType: FieldValidationType;
    hasAiFeature: boolean;
    activePlans: AiSmartReplyContactPlanState[] | undefined;
    isAdmin: boolean;
    userName: string;
    users: UserInfo[];
}

export function ConversationStatusbar({
    contact,
    fieldType,
    hasAiFeature,
    activePlans,
    isAdmin,
    userName,
    users,
}: Props) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setIsOpen(true);
    };
    const handleClose = () => {
        setIsOpen(false);
    };

    //dont check if they have the feature
    const compState = useMemo(() => {
        if (fieldType !== FieldValidationType.usMobilePhone || !activePlans?.length) {
            return undefined;
        } else if (activePlans.length === 1) {
            return {
                title: activePlans[0].planName,
                status: activePlans[0].planItemStatus,
                friendlyStatus: aiSmartReplyContactPlanItemStatusToLabel(
                    activePlans[0].planItemStatus,
                ),
                dateScheduled:
                    activePlans[0].dateScheduled &&
                    activePlans[0].planItemStatus === AiSmartReplyContactPlanItemStatus.Scheduled
                        ? formatRelative(new Date(activePlans[0].dateScheduled), new Date())
                        : undefined,
            };
        } else {
            return {
                title: `${activePlans.length} active plans`,
                status: undefined,
                friendlyStatus: 'View',
                dateScheduled: undefined,
            };
        }
    }, [activePlans, fieldType]);

    if (!compState) return null;

    return (
        <>
            <Paper
                sx={theme => {
                    return {
                        transition: 'height 250ms ease-in-out',
                        backgroundColor: theme.palette.pageNavigationBar.main,
                        color: theme.palette.getContrastText(theme.palette.pageNavigationBar.main),
                        paddingBottom: '2rem',
                        marginBottom: '-2rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                    };
                }}
                elevation={4}
                onClick={handleClick}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    p={1}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PsychologyIcon fontSize="large" />
                        <Stack direction="column">
                            <Typography variant="h6">{compState.title}</Typography>
                            <Typography variant="body2">AI Smart Reply</Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Stack direction="column" p={1}>
                            <Typography variant="body1" align="right">
                                {compState.friendlyStatus}
                            </Typography>
                            {compState.dateScheduled && (
                                <Typography variant="body2" align="right">
                                    {compState.dateScheduled}
                                </Typography>
                            )}
                        </Stack>
                        {compState.status === AiSmartReplyContactPlanItemStatus.Scheduled && (
                            <ScheduleSendIcon fontSize="large" />
                        )}
                        {compState.status === AiSmartReplyContactPlanItemStatus.Sending && (
                            <RocketLaunchIcon fontSize="large" />
                        )}
                        {compState.status === AiSmartReplyContactPlanItemStatus.Unscheduled && (
                            <TryIcon fontSize="large" />
                        )}
                        {compState.status === AiSmartReplyContactPlanItemStatus.Failed && (
                            <ReplayIcon fontSize="large" />
                        )}
                    </Stack>
                </Stack>
            </Paper>
            <SmartReplyDialog
                onCloseHandler={handleClose}
                activePlans={activePlans}
                contact={contact}
                isDialogOpen={isOpen}
                isAdmin={isAdmin}
                userName={userName}
                users={users}
                hasAiFeature={hasAiFeature}
            />
        </>
    );
}
